import { Card } from "@/components/ui/card";
import { motion } from "@/components/ui/motion-provider";
import { Mail, Inbox, Send } from "lucide-react";
import { GitHubSignInButton } from "@/components/common/GitHubSignInButton";

const benefits = [
  {
    icon: <Mail className="w-5 h-5" />,
    title: "Reach out directly",
    description: "Compose and send emails to developers without ever leaving DevFinder.",
  },
  {
    icon: <Inbox className="w-5 h-5" />,
    title: "Unified inbox",
    description: "All replies land in your DevFinder inbox — no tab-switching, no lost threads.",
  },
  {
    icon: <Send className="w-5 h-5" />,
    title: "Full thread history",
    description: "Every conversation is tied to a developer profile so context is always at hand.",
  },
];

const mockEmails = [
  {
    initials: "JD",
    color: "bg-violet-500",
    name: "Jane Doe",
    subject: "Re: Frontend Engineer role at Acme",
    snippet: "Thanks for reaching out! I'd love to learn more about the team...",
    time: "2m ago",
    unread: true,
  },
  {
    initials: "MK",
    color: "bg-blue-500",
    name: "Mark Kim",
    subject: "Re: Senior React developer opportunity",
    snippet: "I'm currently open to new roles and your team sounds exciting...",
    time: "1h ago",
    unread: true,
  },
  {
    initials: "SL",
    color: "bg-emerald-500",
    name: "Sara Lee",
    subject: "Re: DevFinder outreach",
    snippet: "Hi! Yes, definitely interested. Could you share more details...",
    time: "3h ago",
    unread: false,
  },
];

export function GmailSection() {
  return (
    <section className="w-full py-16 sm:py-24">
      <div className="container px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left — copy */}
          <motion.div
            className="flex flex-col gap-6"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {/* Badge */}
            <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <Mail className="w-3.5 h-3.5" />
              New — Gmail Integration
            </div>

            {/* Headline */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              All Developer Communication,{" "}
              <span className="text-primary">Right Inside DevFinder</span>
            </h2>

            {/* Subtext */}
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-lg">
              Connect your Gmail account once and every message to every developer
              lives inside DevFinder. No more juggling email clients — your entire
              outreach pipeline, search, and inbox in one place.
            </p>

            {/* Benefit bullets */}
            <ul className="flex flex-col gap-4">
              {benefits.map((benefit) => (
                <li key={benefit.title} className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0 rounded-lg bg-primary/10 p-2 text-primary">
                    {benefit.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{benefit.title}</p>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="pt-2">
              <GitHubSignInButton variant="faq" />
            </div>
          </motion.div>

          {/* Right — mock inbox */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <Card className="overflow-hidden border border-border/60 shadow-2xl bg-card">
              {/* Inbox header */}
              <div className="flex items-center justify-between border-b border-border/60 bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Inbox className="w-4 h-4 text-primary" />
                  Inbox
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                    2
                  </span>
                </div>
                {/* Fake window controls */}
                <div className="flex gap-1.5">
                  <span className="block h-2.5 w-2.5 rounded-full bg-red-400/70" />
                  <span className="block h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
                  <span className="block h-2.5 w-2.5 rounded-full bg-green-400/70" />
                </div>
              </div>

              {/* Email rows */}
              <ul className="divide-y divide-border/40">
                {mockEmails.map((email) => (
                  <li
                    key={email.name}
                    className={`flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-muted/30 ${
                      email.unread ? "bg-primary/5" : "bg-transparent"
                    }`}
                  >
                    {/* Avatar */}
                    <div
                      className={`shrink-0 flex items-center justify-center rounded-full ${email.color} text-white text-xs font-bold h-8 w-8 mt-0.5`}
                    >
                      {email.initials}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className={`truncate text-sm ${
                            email.unread
                              ? "font-semibold text-foreground"
                              : "font-medium text-muted-foreground"
                          }`}
                        >
                          {email.name}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {email.time}
                        </span>
                      </div>
                      <p
                        className={`truncate text-sm ${
                          email.unread ? "font-medium text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {email.subject}
                      </p>
                      <p className="truncate text-xs text-muted-foreground mt-0.5">
                        {email.snippet}
                      </p>
                    </div>

                    {/* Unread dot */}
                    {email.unread && (
                      <div className="mt-2 shrink-0 h-2 w-2 rounded-full bg-primary" />
                    )}
                  </li>
                ))}
              </ul>

              {/* Compose bar */}
              <div className="border-t border-border/60 bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-3 py-2 text-sm text-muted-foreground shadow-sm">
                  <Mail className="w-4 h-4 shrink-0" />
                  <span className="flex-1 truncate">
                    Compose a new message to a developer…
                  </span>
                  <Send className="w-4 h-4 shrink-0 text-primary" />
                </div>
              </div>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
